import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Image, Video, Play, X, ChevronLeft, ChevronRight, ZoomIn, FolderOpen } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { imageGallerySchema } from "@/utils/schemas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["All", "Junk Removal", "Hauling", "Clean Out", "Demolition"];

const defaultCMS = {
  hero: {
    headline1: "Project",
    headline2: "Gallery",
    description: "See examples of our work across the DFW metroplex. From residential clean outs to commercial hauling projects."
  }
};

// Gallery Card Component with Before/After display options
const GalleryCard = ({ item, index, getImageUrl, onImageClick }) => {
  const [showBefore, setShowBefore] = useState(false);
  const [imgError, setImgError] = useState(false);
  const hasBefore = item.before_image && item.before_image.length > 0;
  const displayMode = item.display_mode || "toggle";
  
  const handleImageError = () => {
    setImgError(true);
  };

  // Hide the entire card if the main image is broken
  if (imgError) return null;
  
  // Side-by-side mode
  if (hasBefore && displayMode === "side-by-side") {
    return (
      <div 
        data-testid={`gallery-item-${index}`}
        className="bg-white shadow-sm rounded-sm overflow-hidden"
      >
        <div className="grid grid-cols-2 gap-1">
          <div className="relative cursor-pointer group overflow-hidden" onClick={() => onImageClick(getImageUrl(item.before_image), `${item.title} - Before`)}>
            <img 
              src={getImageUrl(item.before_image)} 
              alt={`${item.title} - Before`}
              className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
              onError={handleImageError}
            />
            <span className="absolute top-2 left-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded z-10">
              BEFORE
            </span>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
            </div>
          </div>
          <div className="relative cursor-pointer group overflow-hidden" onClick={() => onImageClick(getImageUrl(item.image_url), `${item.title} - After`)}>
            <img 
              src={getImageUrl(item.image_url)} 
              alt={`${item.title} - After`}
              className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
              onError={handleImageError}
            />
            <span className="absolute top-2 left-2 bg-[#b51a00] text-white text-xs font-bold px-2 py-1 rounded z-10">
              AFTER
            </span>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
            </div>
          </div>
        </div>
        {/* Info below the image */}
        <div className="p-4">
          <span className="text-xs uppercase tracking-widest text-[#b51a00] font-bold">
            {item.category}
          </span>
          <h3 
            className="text-lg font-bold text-[#1a1a1a] uppercase mt-1"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {item.title}
          </h3>
          <p className="text-gray-600 text-sm mt-1">
            {item.description}
          </p>
        </div>
      </div>
    );
  }
  
  // Toggle mode (default) or no before image
  const currentImage = showBefore && hasBefore 
    ? getImageUrl(item.before_image) 
    : getImageUrl(item.image_url);
  
  return (
    <div 
      data-testid={`gallery-item-${index}`}
      className="bg-white shadow-sm rounded-sm overflow-hidden"
    >
      {/* Image container */}
      <div className="relative cursor-pointer group overflow-hidden" onClick={() => onImageClick(currentImage, showBefore ? `${item.title} - Before` : item.title)}>
        <img 
          src={currentImage} 
          alt={showBefore ? `${item.title} - Before` : item.title}
          className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
          onError={handleImageError}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
        </div>
        
        {/* Before/After Toggle - only for toggle mode */}
        {hasBefore && displayMode === "toggle" && (
          <div className="absolute top-3 left-3 z-20 flex bg-black/70 rounded-full overflow-hidden text-xs font-bold">
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(false); }}
              className={`px-3 py-1.5 transition-colors ${!showBefore ? 'bg-[#b51a00] text-white' : 'text-gray-300 hover:text-white'}`}
            >
              AFTER
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setShowBefore(true); }}
              className={`px-3 py-1.5 transition-colors ${showBefore ? 'bg-[#b51a00] text-white' : 'text-gray-300 hover:text-white'}`}
            >
              BEFORE
            </button>
          </div>
        )}
      </div>
      
      {/* Info below the image - always visible */}
      <div className="p-4">
        <span className="text-xs uppercase tracking-widest text-[#b51a00] font-bold">
          {item.category}
        </span>
        <h3 
          className="text-lg font-bold text-[#1a1a1a] uppercase mt-1"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          {item.title}
        </h3>
        <p className="text-gray-600 text-sm mt-1">
          {item.description}
        </p>
      </div>
    </div>
  );
};

// Skeleton loader component for Gallery page
const GallerySkeleton = () => (
  <div data-testid="gallery-page" className="pt-20">
    <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl animate-pulse">
          <div className="h-4 w-24 bg-white/20 rounded mb-4"></div>
          <div className="h-16 w-80 bg-white/20 rounded mb-6"></div>
          <div className="h-6 w-96 bg-white/20 rounded"></div>
        </div>
      </div>
    </section>
    <section className="bg-[#1a1a1a] border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1 animate-pulse">
          <div className="h-12 w-28 bg-white/10 rounded"></div>
          <div className="h-12 w-28 bg-white/10 rounded"></div>
        </div>
      </div>
    </section>
    <section className="py-20 md:py-32 bg-[#f4f4f5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-sm shadow-sm">
              <div className="aspect-video bg-gray-200"></div>
              <div className="p-4">
                <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                <div className="h-5 w-32 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);

// Helper to get full image URL (handles both relative and absolute URLs)
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return "";
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }
  // Handle both /uploads/ and /api/uploads/ paths for legacy data
  if (imageUrl.startsWith("/uploads/")) {
    return `${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
};

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

export default function Gallery() {
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("photos");
  const [activeCategory, setActiveCategory] = useState("All");
  const [cms, setCms] = useState(defaultCMS);
  const [lightbox, setLightbox] = useState({ open: false, src: "", alt: "", images: [], currentIndex: 0 });

  const openLightbox = useCallback((src, alt, images = [], currentIndex = 0) => {
    setLightbox({ open: true, src, alt, images, currentIndex });
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, src: "", alt: "", images: [], currentIndex: 0 });
    document.body.style.overflow = "";
  }, []);

  const navigateLightbox = useCallback((direction) => {
    setLightbox(prev => {
      if (!prev.images || prev.images.length === 0) return prev;
      const newIndex = (prev.currentIndex + direction + prev.images.length) % prev.images.length;
      return { ...prev, currentIndex: newIndex, src: getImageUrl(prev.images[newIndex]), alt: `Image ${newIndex + 1}` };
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft" && lightbox.images?.length > 0) navigateLightbox(-1);
      if (e.key === "ArrowRight" && lightbox.images?.length > 0) navigateLightbox(1);
    };
    if (lightbox.open) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [lightbox.open, lightbox.images, closeLightbox, navigateLightbox]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, videosRes, projectsRes, cmsRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/videos`).catch(() => ({ data: [] })),
          axios.get(`${API}/projects`).catch(() => ({ data: [] })),
          axios.get(`${API}/cms/page/gallery`).catch(() => ({ data: [] }))
        ]);
        setGallery(galleryRes.data);
        setVideos(videosRes.data || []);
        setProjects(projectsRes.data || []);
        
        if (cmsRes.data?.length > 0) {
          const content = { ...defaultCMS };
          cmsRes.data.forEach(item => {
            if (item.section && item.content) {
              content[item.section] = { ...content[item.section], ...item.content };
            }
          });
          setCms(content);
        }
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
        setCmsLoaded(true);
      }
    };
    fetchData();
  }, []);

  const gallerySchemas = useMemo(() => {
    const images = gallery.slice(0, 20).map(item => ({
      url: item.image_url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url,
      alt: item.title || item.caption || "Project photo",
      caption: item.caption || "",
    }));
    return images.length > 0 ? [imageGallerySchema(images)] : [];
  }, [gallery]);

  // Show skeleton while initial data loads  
  if (!cmsLoaded) {
    return <GallerySkeleton />;
  }

  const filteredGallery = activeCategory === "All" 
    ? gallery 
    : gallery.filter(item => item.category === activeCategory);

  return (
    <div data-testid="gallery-page" className="pt-20">
      <SEOHead
        title="Project Gallery - Junk Removal & Cleanout Photos"
        description="Browse photos of completed junk removal, cleanout, hauling, and demolition projects by Overhaul Ops LLC in the DFW metroplex."
        keywords="junk removal photos, before after cleanout, project gallery, DFW junk removal work"
        schemas={gallerySchemas}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Gallery", url: "/gallery" }]}
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-[#1a1a1a] noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
              Our Work
            </span>
            <h1 
              className="text-5xl md:text-7xl font-black text-white uppercase leading-none mt-2 mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.02em' }}
            >
              {cms.hero.headline1}<br />
              <span className="text-[#b51a00]">{cms.hero.headline2}</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {cms.hero.description}
            </p>
          </div>
        </div>
      </section>

      {/* Main Tabs - Photos/Projects/Videos */}
      <section className="bg-[#1a1a1a] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("photos")}
              data-testid="tab-photos"
              className={`flex items-center gap-2 px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all ${
                activeTab === "photos"
                  ? "bg-[#b51a00] text-white"
                  : "bg-transparent text-gray-400 hover:text-white"
              }`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <Image className="w-5 h-5" />
              Before/After
            </button>
            <button
              onClick={() => setActiveTab("projects")}
              data-testid="tab-projects"
              className={`flex items-center gap-2 px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all ${
                activeTab === "projects"
                  ? "bg-[#b51a00] text-white"
                  : "bg-transparent text-gray-400 hover:text-white"
              }`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <FolderOpen className="w-5 h-5" />
              Projects
            </button>
            <button
              onClick={() => setActiveTab("videos")}
              data-testid="tab-videos"
              className={`flex items-center gap-2 px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all ${
                activeTab === "videos"
                  ? "bg-[#b51a00] text-white"
                  : "bg-transparent text-gray-400 hover:text-white"
              }`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <Video className="w-5 h-5" />
              Videos
            </button>
          </div>
        </div>
      </section>

      {/* Photo Filter Tabs - Only show for photos tab */}
      {activeTab === "photos" && (
        <section className="bg-white border-b sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex overflow-x-auto py-4 gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-3 font-bold uppercase tracking-wider text-sm whitespace-nowrap rounded-sm transition-all cursor-pointer ${
                    activeCategory === category
                      ? "bg-[#b51a00] text-white"
                      : "bg-[#f4f4f5] text-[#4a4a4a] hover:bg-[#e5e5e5]"
                  }`}
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content Section */}
      <section data-testid="gallery-grid" className="py-12 md:py-16 bg-[#f4f4f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
            </div>
          ) : activeTab === "photos" ? (
            /* Photos Grid */
            filteredGallery.length > 0 ? (
              <div className="gallery-grid">
                {filteredGallery.map((item, index) => (
                  <GalleryCard key={item.id || index} item={item} index={index} getImageUrl={getImageUrl} onImageClick={openLightbox} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-[#707070] text-lg">No projects found in this category.</p>
              </div>
            )
          ) : activeTab === "projects" ? (
            /* Projects Grid */
            projects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {projects.map((project, index) => {
                  const coverImg = project.cover_image || (project.images && project.images[0]);
                  const imageCount = project.images?.length || 0;
                  return (
                    <div
                      key={project.id || index}
                      data-testid={`project-item-${index}`}
                      className="bg-white rounded-sm shadow-sm overflow-hidden group cursor-pointer"
                      onClick={() => {
                        if (project.images && project.images.length > 0) {
                          openLightbox(
                            getImageUrl(project.images[0]),
                            `${project.title} - Image 1`,
                            project.images,
                            0
                          );
                        }
                      }}
                    >
                      <div className="relative overflow-hidden">
                        {coverImg ? (
                          <img
                            src={getImageUrl(coverImg)}
                            alt={project.title}
                            className="w-full aspect-video object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full aspect-video bg-gray-200 flex items-center justify-center">
                            <FolderOpen className="w-12 h-12 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full px-4 py-2 flex items-center gap-2">
                            <ZoomIn className="w-4 h-4 text-[#1a1a1a]" />
                            <span className="text-sm font-bold text-[#1a1a1a]">View {imageCount} Photos</span>
                          </div>
                        </div>
                        {imageCount > 1 && (
                          <span className="absolute top-3 right-3 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded">
                            {imageCount} Photos
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <span className="text-xs uppercase tracking-widest text-[#b51a00] font-bold">
                          {project.category}
                        </span>
                        <h3
                          className="text-lg font-bold text-[#1a1a1a] uppercase mt-1"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                        >
                          {project.title}
                        </h3>
                        {project.description && (
                          <p className="text-gray-600 text-sm mt-1">{project.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-[#707070] text-lg">Projects coming soon!</p>
                <p className="text-gray-500 text-sm mt-2">Check back for project showcases.</p>
              </div>
            )
          ) : (
            /* Videos Grid */
            videos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {videos.map((video, index) => {
                  const videoId = getYouTubeId(video.youtube_url);
                  return (
                    <div 
                      key={video.id || index}
                      data-testid={`video-item-${index}`}
                      className="bg-white rounded-lg shadow-sm overflow-hidden"
                    >
                      {videoId ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}`}
                          title={video.title}
                          className="w-full aspect-video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="block">
                          <div className="w-full aspect-video bg-gray-200 flex items-center justify-center">
                            <Play className="w-12 h-12 text-gray-400" />
                          </div>
                        </a>
                      )}
                      <div className="p-4">
                        <h3 
                          className="text-lg font-bold text-[#1a1a1a] uppercase"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                        >
                          {video.title}
                        </h3>
                        {video.description && (
                          <p className="text-gray-600 text-sm mt-1">{video.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-[#707070] text-lg">Videos coming soon!</p>
                <p className="text-gray-500 text-sm mt-2">Check back for project videos and tutorials.</p>
              </div>
            )
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Ready for Your Project?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Let us help you reclaim your space. Get a free quote today!
          </p>
          <Link to="/contact">
            <Button 
              data-testid="gallery-cta-btn"
              className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightbox.open && (
        <div 
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            data-testid="lightbox-close-btn"
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 text-white/80 hover:text-white transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          {/* Navigation arrows for multi-image projects */}
          {lightbox.images && lightbox.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lightbox.images && lightbox.images.length > 1 
              ? `${lightbox.currentIndex + 1} / ${lightbox.images.length} — Use arrows or click outside to close`
              : `${lightbox.alt} — Click outside or press ESC to close`
            }
          </p>
        </div>
      )}
    </div>
  );
}
